import { NATAL_GROUPS, type NatalGroup, type NatalSection } from '@/api/groups-natal'
import { Col } from '@/app/components/Flex/Flex'
import type { NatalChartPlanet } from '@/app/components/NatalChart/NatalChart'
import { Planet } from '@/app/components/Planet/Planet'
import { Text } from '@/app/components/Text/Text'
import { UI } from '@/app/components/UI/UI'
import { useNatal } from '@/app/state/natal'
import { useSession } from '@/app/state/session'
import { ASPECT_NAMES, getNatalAspectQuestion, PLANET_NAMES } from '@/app/utils/natal'
import classNames from 'classnames'
import type { FunctionalComponent } from 'preact'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper/types'
import { useShallow } from 'zustand/shallow'
import styles from './index.module.scss'

const NATAL_TABS = [
  ...NATAL_GROUPS,
  { id: 'aspects', title: 'Аспекты' },
]

export type NatalSectionsAspect = {
  planet1: NatalChartPlanet
  planet2: NatalChartPlanet
  aspect: number
}

function isNatalChartPlanet(name: string): name is NatalChartPlanet {
  return name in PLANET_NAMES
}

type NatalSectionsProps = {
  initialGroupId?: string
  onGroupChange?: (groupId: string) => void
  onSectionClick: (section: NatalSection, group: NatalGroup) => void
  onAspectClick: (groupId: 'aspects', question: string) => void
  onLockedSectionClick: (section: NatalSection, group: NatalGroup) => void
  onClose: () => void
}

export const NatalSections: FunctionalComponent<NatalSectionsProps> = ({
  initialGroupId,
  onGroupChange,
  onSectionClick,
  onAspectClick,
  onLockedSectionClick,
  onClose,
}) => {
  const fetch = useNatal(s => s.fetch)
  const chart = useNatal(useShallow(s => s.chart))
  const hasExactBirthDetails = useSession(s => !!s.profile?.birthTime && !!s.profile?.location)
  const swiperRef = useRef<SwiperType | null>(null)

  const aspects = useMemo(() => {
    const availableAspects = chart?.aspects.flatMap(aspect =>
      isNatalChartPlanet(aspect.planet1) && isNatalChartPlanet(aspect.planet2)
        ? [{
          ...aspect,
          planet1: aspect.planet1,
          planet2: aspect.planet2,
        }]
        : []
    ) ?? []

    return [
      ...availableAspects.filter(aspect => aspect.aspect !== 0),
      ...availableAspects.filter(aspect => aspect.aspect === 0),
    ]
  }, [chart])

  const initialIdx = useMemo(
    () => Math.max(0, NATAL_TABS.findIndex(group => group.id === initialGroupId)),
    [initialGroupId],
  )

  const [activeIdx, setActiveIdx] = useState(initialIdx)

  useEffect(() => {
    fetch()
  }, [])

  useEffect(() => {
    setActiveIdx(initialIdx)

    if (swiperRef.current && swiperRef.current.activeIndex !== initialIdx) {
      swiperRef.current.slideTo(initialIdx, 0)
    }
  }, [initialIdx])

  return (
    <>
      <div
        class={styles.overlay}
        onClick={onClose}
      />

      <UI.Panel
        shadow
        radius='m'
        class={styles.root}
      >
        <UI.Tabs>
          {NATAL_TABS.map((group, idx) => (
            <UI.Tab
              height={47}
              key={group.id}
              active={activeIdx === idx}
              onClick={() => {
                setActiveIdx(idx)
                onGroupChange?.(group.id)
                swiperRef.current?.slideTo(idx)
              }}
            >
              {group.title}
            </UI.Tab>
          ))}
        </UI.Tabs>

        <div class={styles.swiperWrapper}>
          <Swiper
            initialSlide={initialIdx}
            slidesPerView={1}
            onSwiper={(swiper) => {
              swiperRef.current = swiper

              if (swiper.activeIndex !== initialIdx) {
                swiper.slideTo(initialIdx, 0)
              }
            }}
            onSlideChange={(swiper) => {
              setActiveIdx(swiper.activeIndex)
              const group = NATAL_TABS[swiper.activeIndex]

              if (group) {
                onGroupChange?.(group.id)
              }
            }}
          >
            {NATAL_GROUPS.map(group => (
              <SwiperSlide key={group.id} class={styles.swiperSlide}>
                <div class={styles.table}>
                  {group.sections.map(section => {
                    const locked = section.requiresBirthTimeAndLocation
                      && !hasExactBirthDetails

                    return (
                      <button
                        key={section.id}
                        type='button'
                        class={classNames(styles.row, {
                          [styles.locked]: locked,
                        })}
                        onClick={() => {
                          if (locked) {
                            onLockedSectionClick?.(section, group)
                            return
                          }

                          onSectionClick?.(section, group)
                        }}
                      >
                        <Text
                          fontSize={16}
                          class={styles.name}
                        >
                          {section.title}
                        </Text>

                        <Col class={styles.points}>
                          {section.points.map((point, idx) => (
                            point.type === 'planet'
                              ? (
                                <span key={idx} class={styles.planet}>
                                  <Planet planet={point.value} />
                                </span>
                              )
                              : null
                          ))}
                        </Col>
                      </button>
                    )
                  })}
                </div>
              </SwiperSlide>
            ))}

            <SwiperSlide class={styles.swiperSlide}>
              <div class={styles.table}>
                {aspects.map((aspect) => {
                  const aspectName = ASPECT_NAMES[aspect.aspect] ?? `${aspect.aspect}°`

                  return (
                    <button
                      key={`${aspect.planet1}-${aspect.planet2}-${aspect.aspect}`}
                      type='button'
                      class={styles.row}
                      onClick={() => {
                        onAspectClick('aspects', getNatalAspectQuestion(aspect))
                      }}
                    >
                      <Text
                        fontSize={16}
                        class={styles.name}
                      >
                        {aspectName}
                      </Text>

                      <Col>
                        <span class={styles.planet}>
                          <Planet planet={aspect.planet1} />
                        </span>
                        <span class={styles.planet}>
                          <Planet planet={aspect.planet2} />
                        </span>
                      </Col>
                    </button>
                  )
                })}
              </div>
            </SwiperSlide>
          </Swiper>
        </div>
      </UI.Panel>
    </>
  )
}
